import { MeshBuilder, type Mesh } from "@babylonjs/core";
import { Btn, POCKET_VARS, HeroId, PK, Slot, ZK, hv } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import { mallet, trim } from "../../audio/synth.ts";
import type { ClientHero } from "../types.ts";
import { orbBounce, pocketAmplifier, pocketBeacon, pocketBombardFire, pocketJetDash, pocketReload, pocketRepairFire } from "../audio-cues.ts";

const theme = {
  bpm: 156,
  octave: 1,
  voice: mallet,
  notes: [
    [7, 0.25],
    [9, 0.25],
    [12, 0.5],
    [null, 0.25],
    [7, 0.25],
    [14, 0.5],
    [12, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

type Gear = { blaster: Mesh; tail: Mesh; ears: Mesh[]; goggles: Mesh; jets: Mesh[] };

const hero: ClientHero<Gear> = {
  id: HeroId.PocketRaccoon,
  bulk: 0.78,
  shape: { head: 1.18, torso: 0.82, arms: 0.8, legs: 0.72 },
  gear(g) {
    const { R, chestH, rig, torso, hand, dark, main, add, glow, toon, scene } = g;
    const blaster = add(MeshBuilder.CreateBox("blaster", { width: 0.28, height: 0.28, depth: 1.0 }, scene), rig.handR ?? hand, dark);
    blaster.position.z = -0.25;
    const drum = add(MeshBuilder.CreateCylinder("drum", { diameter: 0.34, height: 0.24, tessellation: 12 }, scene), rig.handR ?? hand, main);
    drum.position.set(0, -0.14, -0.15);
    const muzzle = add(MeshBuilder.CreateCylinder("muzzle", { diameter: 0.18, height: 0.16, tessellation: 10 }, scene), rig.handR ?? hand, glow("#7dffb0"));
    muzzle.rotation.x = Math.PI / 2;
    muzzle.position.z = -0.72;
    const ears = [-1, 1].map((side) => {
      const ear = add(MeshBuilder.CreateCylinder("raccoon-ear", { diameterTop: 0, diameterBottom: 0.22, height: 0.28, tessellation: 3 }, scene), rig.head, toon("#5a3c2b"));
      ear.rotation.z = -side * 0.45;
      ear.position.set(side * 0.18, 0.25, 0);
      return ear;
    });
    const goggles = add(MeshBuilder.CreateBox("mask-goggles", { width: 0.46, height: 0.09, depth: 0.04 }, scene), rig.head, dark);
    goggles.position.set(0, 0.03, -0.22);
    const tail = add(MeshBuilder.CreateCylinder("ringtail", { diameterTop: 0.12, diameterBottom: 0.22, height: 0.85, tessellation: 10 }, scene), rig.torso, toon("#5a3c2b"));
    tail.rotation.x = Math.PI / 2;
    tail.position.set(0, -0.18, 0.48);
    for (let i = 0; i < 4; i++) {
      const stripe = add(MeshBuilder.CreateTorus("tail-stripe", { diameter: 0.19 - i * 0.015, thickness: 0.018, tessellation: 12 }, scene), rig.torso, toon(i % 2 ? "#f5f0e6" : "#1e1714"));
      stripe.rotation.x = Math.PI / 2;
      stripe.position.set(0, -0.18, 0.24 + i * 0.11);
    }
    const jets: Mesh[] = [];
    for (const side of [-1, 1]) {
      const tank = add(MeshBuilder.CreateCylinder("thruster", { diameter: 0.2, height: chestH * 0.95, tessellation: 10 }, scene), torso, dark);
      tank.position.set(side * 0.15, chestH * 0.55, R * 0.8);
      const jet = add(MeshBuilder.CreateCylinder("jet", { diameterTop: 0.16, diameterBottom: 0.05, height: 0.16, tessellation: 8 }, scene), torso, glow("#7dffb0"));
      jet.position.set(side * 0.15, chestH * 0.02, R * 0.8);
      jets.push(jet);
    }
    return { blaster, tail, ears, goggles, jets };
  },
  animate(gear, f) {
    gear.tail.rotation.y = Math.sin(f.t * 10) * 0.35;
    const flame = f.v[0] < 98 && !f.grounded ? 1.8 : 1;
    for (const j of gear.jets) j.scaling.y = flame;
    return f.action === 2 ? "holding-right-shoot" : "holding-right";
  },
  hud(c, eid) {
    return [{ label: "Rocket fuel", value: hv(c, eid, POCKET_VARS.fuel) / 100 }];
  },
  lifting(c, eid, buttons, grounded) {
    return !grounded && !hv(c, eid, POCKET_VARS.climbing) && hv(c, eid, POCKET_VARS.fuel) > 1 && !!(buttons & Btn.Jump);
  },
  audio: {
    weight: "light",
    fire: (ctx) => ({ key: ctx?.alt ? "fire:pocket-raccoon:orb" : "fire:pocket-raccoon:bombard", cue: ctx?.alt ? trim(3, pocketRepairFire) : trim(2.5, pocketBombardFire) }),
    reload: pocketReload,
    ability: {
      [Slot.Ability1]: trim(1, pocketJetDash),
      [Slot.Ability2]: pocketBeacon,
      [Slot.Ultimate]: pocketAmplifier,
    },
    theme,
    themeTrim: { heroic: 2, hostile: -6.2, tease: 2.4 },
    loops: ({ v, alive }) => (alive && v[1] > 0 ? [{ key: "climb", build: loops.climb, gain: 0.7 }] : []),
    flight: { [PK.RepairOrb]: loops.flight("orb") },
    projectileBounces: { [PK.RepairOrb]: orbBounce },
    zoneLoops: {
      [ZK.Beacon]: { build: loops.beacon, gain: 0.8, range: 36 },
      [ZK.Amplifier]: { build: loops.amplifier, gain: 0.8, range: 36 },
    },
    selfLift: ({ alive }) => (alive ? loops.jets : undefined),
  },
  icons: {
    [Slot.Primary]: "M5 12h14M8 8l8 8M8 16l8-8",
    [Slot.Secondary]: "M7 12a5 5 0 1010 0a5 5 0 10-10 0M12 8v8",
    [Slot.Ability1]: "M6 16l6-10 6 10M8 16h8",
    [Slot.Ability2]: "M7 17h10V8H7zM10 12h4",
    [Slot.Ultimate]: "M6 16h12M8 12h8M10 8h4M12 4v16",
    passive: "M8 18c0-6 8-6 8-12M8 12h8",
  },
};

export default hero;
